import { InjectionToken, Injector, Provider, Type } from '@angular/core';
import { Transition } from '@uirouter/core';

export type BasePurchasePlan = {
  name: string;
  productId?: string;
  productCode?: string;
  licenses?: number;
  isMonthly?: boolean;
  monthly?: { billAmount: number };
  yearly?: { billAmount: number };
};

export type BasePurchaseAddress = {
  id?: string;
  name?: string;
  street?: string;
  unit?: string;
  city?: string;
  country?: string;
  postalCode?: string;
  province?: string;
  validationError?: string | boolean;
};

export type BasePurchaseContact = {
  username?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  telephone?: string;
};

export type BasePurchaseEstimateAdjustment = {
  amount?: number;
  description?: string;
  [key: string]: unknown;
};

export type BasePurchaseEstimate = {
  total?: number;
  subTotal?: number;
  volumeSubtotal?: number;
  unlimitedSubtotal?: number;
  totalTax?: number;
  couponAmount?: number;
  currency?: string;
  taxes?: BasePurchaseEstimateAdjustment[];
  coupons?: BasePurchaseEstimateAdjustment[];
  estimateError?: string;
  price?: unknown;
  [key: string]: unknown;
};

export type PurchaseAddOnType = 'interactive' | 'ai-credits';

export type BasePurchaseDetails = {
  volumePlan?: BasePurchasePlan;
  unlimitedPlan?: BasePurchasePlan;
  addOnPlan?: BasePurchasePlan;
  couponCode?: string;
  billingAddress?: BasePurchaseAddress;
  contact?: BasePurchaseContact;
  estimate?: BasePurchaseEstimate;
  checkoutError?: string | null;
  reloadingCompany?: boolean;
} & Partial<Record<PurchaseAddOnType, BasePurchasePlan>>;

export abstract class BasePurchaseService {
  abstract loading: boolean;
  abstract purchase: BasePurchaseDetails;

  get forceCreditCardPayment(): boolean {
    return false;
  }

  abstract init(): Promise<void>;
  abstract getEstimate(): Promise<void>;
  abstract validatePaymentMethod(): Promise<void>;
  abstract preparePaymentIntent(): Promise<void>;
  abstract completePayment(): Promise<void>;
  abstract onCompanyReload(reloadCallback: () => void): void;
}

export const BASE_PURCHASE_SERVICE = new InjectionToken<BasePurchaseService>(
  'BasePurchaseService',
);

export type BasePurchaseServiceTypes = {
  aiCredits: Type<BasePurchaseService>;
  interactive: Type<BasePurchaseService>;
};

export function provideBasePurchaseService(
  serviceTypes: BasePurchaseServiceTypes,
): Provider {
  return {
    provide: BASE_PURCHASE_SERVICE,
    useFactory: (transition: Transition, injector: Injector) => {
      switch (transition.params().purchaseIntent) {
        case 'interactive':
          return injector.get(serviceTypes.interactive);
        case 'ai-credits':
        default:
          return injector.get(serviceTypes.aiCredits);
      }
    },
    deps: [Transition, Injector],
  };
}
